import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import api from "../../lib/api";
import { ANALYSIS_PHASES } from "../../lib/sales";

const PHASE_MS = 620;
const MIN_MS = ANALYSIS_PHASES.length * PHASE_MS + 500;

export default function HealthCheckAnalysis() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState(0);
  const [done, setDone] = useState(false);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    const iv = setInterval(() => setPhase((p) => Math.min(p + 1, ANALYSIS_PHASES.length)), PHASE_MS);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    let mounted = true;
    api.post(`/sales/health-checks/${id}/run`).then(() => {
      const wait = Math.max(0, MIN_MS - (Date.now() - startedAt.current));
      setTimeout(() => { if (mounted) { setDone(true); setTimeout(() => navigate(`/sales/check/${id}`), 700); } }, wait);
    }).catch(() => { if (mounted) navigate(`/sales/check/${id}`); });
    return () => { mounted = false; };
  }, [id, navigate]);

  const pct = Math.round(Math.min(phase, ANALYSIS_PHASES.length) / ANALYSIS_PHASES.length * 100);

  return (
    <div className="fixed inset-0 z-[60] overflow-hidden" style={{ background: "radial-gradient(120% 90% at 50% 0%, #1E2416 0%, #0C0E08 60%)" }}>
      {/* scanning glow */}
      <motion.div className="absolute left-1/2 -translate-x-1/2" style={{ top: -260, width: 720, height: 720, background: "radial-gradient(circle, rgba(147,192,32,.28), transparent 65%)", filter: "blur(10px)" }}
        animate={{ opacity: [0.5, 0.9, 0.5] }} transition={{ duration: 2.4, repeat: Infinity }} />

      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6">
        {/* Radar dial */}
        <div className="relative" style={{ width: 220, height: 220 }}>
          <svg width="220" height="220" className="absolute inset-0 -rotate-90">
            <circle cx="110" cy="110" r="96" fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="6" />
            <motion.circle cx="110" cy="110" r="96" fill="none" stroke="#B6E14F" strokeWidth="6" strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 96} animate={{ strokeDashoffset: 2 * Math.PI * 96 * (1 - pct / 100) }} transition={{ ease: "easeOut" }} />
          </svg>
          {[78, 58, 38].map((r, i) => (
            <span key={i} className="absolute rounded-full" style={{ inset: `${110 - r}px`, width: r * 2, height: r * 2, border: "1px solid rgba(182,225,79,.12)" }} />
          ))}
          <motion.div className="absolute left-1/2 top-1/2 origin-left" style={{ width: 96, height: 2, background: "linear-gradient(90deg, rgba(182,225,79,.9), transparent)" }}
            animate={{ rotate: 360 }} transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }} />
          <div className="absolute inset-0 grid place-items-center">
            {done ? (
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="grid place-items-center w-16 h-16 rounded-full" style={{ background: "var(--grad-green)" }}>
                <Check size={30} strokeWidth={3} color="#16180F" />
              </motion.div>
            ) : (
              <div className="text-center">
                <div className="text-4xl font-extrabold text-white tabular-nums">{pct}%</div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] mt-1" style={{ color: "rgba(255,255,255,.45)" }}>Analysing</div>
              </div>
            )}
          </div>
        </div>

        <h2 className="text-xl font-extrabold text-white mt-8">{done ? "Analysis complete" : "Growth is auditing the digital presence"}</h2>
        <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,.5)" }}>{done ? "Preparing your Social Health Score…" : "Every figure will be traceable to its source"}</p>

        {/* Phase list */}
        <div className="mt-8 w-full max-w-md grid grid-cols-2 gap-x-8 gap-y-2.5">
          {ANALYSIS_PHASES.map((label, i) => {
            const complete = i < phase; const active = i === phase && !done;
            return (
              <div key={label} className="flex items-center gap-2.5 text-sm" data-testid={`phase-${i}`}>
                <span className="grid place-items-center w-5 h-5 rounded-full shrink-0 transition-colors" style={{ background: complete ? "rgba(182,225,79,.2)" : "rgba(255,255,255,.06)" }}>
                  {complete ? <Check size={12} strokeWidth={3} color="#B6E14F" /> : active ? <Loader2 size={12} className="animate-spin" color="rgba(255,255,255,.6)" /> : <span className="w-1 h-1 rounded-full" style={{ background: "rgba(255,255,255,.25)" }} />}
                </span>
                <span style={{ color: complete ? "rgba(255,255,255,.85)" : active ? "#fff" : "rgba(255,255,255,.4)" }}>{label}</span>
              </div>
            );
          })}
        </div>

        <div className="mt-8 inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-3 py-1.5" style={{ color: "rgba(182,225,79,.9)", background: "rgba(182,225,79,.08)" }}>
          <ShieldCheck size={13} /> Presentation is dramatic — the data is defensible
        </div>
      </div>
    </div>
  );
}
